<template>
  <!-- Modal -->
  <div
    class="modal"
    id="trainingseinheit-anlegen-modal"
    tabindex="-1"
    ref="modal"
    aria-labelledby="trainingseinheit-anlegen-modal-label"
    aria-hidden="true"
  >
    <div
      class="modal-dialog shadow-md modal-fullscreen-md-down modal-dialog-centered"
      ref="modalDialog"
    >
      <div class="modal-content border-ruderclub-dark-green">
        <div class="modal-header bg-ruderclub-dark-green">
          <h5 class="modal-title" id="trainingseinheit-anlegen-modal-label">
            Trainingseinheit anlegen
          </h5>
          <button
            type="button"
            class="btn-close bg-light"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="closeModal"
          ></button>
        </div>
        <div class="modal-body">
          <div class="alert alert-info" v-if="!targetComponentNameAfterSubmit">
            Hier kannst Du die Daten aus Deinem letzten Training angeben und
            speichern. Die Daten werden Dir dann auf dem Dashboard in einem
            Diagramm und tabellarisch dargestellt.
          </div>
          <div
            class="alert alert-success"
            v-if="targetComponentNameAfterSubmit"
          >
            Hier sind die Daten aus Deinem Training. Gleiche die Daten mit denen
            Deines Gerätes ab und überschreibe falsche oder ungenaue Werte. Die
            Daten werden Dir dann auf dem Dashboard in einem Diagramm und
            tabellarisch dargestellt.
          </div>
          <form @submit.prevent="submitForm" class="mt-4">
            <!-- Benutzer auswählen (nur für Admins sichtbar) -->
            <div v-if="istAdmin" class="mb-4 input-group">
              <span class="input-group-text">
                <span class="material-symbols-outlined"> person </span>
              </span>
              <select
                id="benutzer"
                v-model="form.benutzer"
                class="form-select"
                required
              >
                <option
                  v-for="benutzer in benutzerListe"
                  :key="benutzer.id"
                  :value="benutzer.id"
                >
                  {{ benutzer.name }}
                </option>
              </select>
            </div>

            <!-- Titel -->
            <div class="mb-4 input-group">
              <span class="input-group-text" title="Titel">
                <span class="material-symbols-outlined"> title </span>
              </span>
              <input
                title="Titel"
                type="text"
                id="titel"
                v-model="form.titel"
                class="form-control"
                required
              />
            </div>

            <!-- Datum und Uhrzeit -->
            <div class="mb-4 input-group">
              <span class="input-group-text" title="Datum und Uhrzeit">
                <span class="material-symbols-outlined"> calendar_clock </span>
              </span>
              <input
                title="Datum und Uhrzeit"
                type="datetime-local"
                id="datumUhrzeit"
                v-model="form.datumUhrzeit"
                class="form-control"
                required
              />
            </div>

            <!-- Trainingsmethode -->
            <div class="mb-4 input-group">
              <span class="input-group-text" title="Trainingsmethode">
                <span class="material-symbols-outlined">hail</span>
              </span>
              <select
                id="trainingsmethode"
                v-model="form.trainingsmethode"
                class="form-select"
                required
              >
                <option value="" disabled>Bitte wählen...</option>
                <option
                  v-for="methode in trainingsmethodenListe"
                  :key="methode.id"
                  :value="methode.id"
                >
                  {{ methode.name }}
                </option>
              </select>
            </div>

            <!-- Distanz -->
            <div class="mb-4 input-group">
              <span class="input-group-text" title="Distanz">
                <span class="material-symbols-outlined"> straighten </span>
              </span>
              <input
                title="Distanz"
                type="text"
                id="distanz"
                v-model="formattedDistanz"
                class="form-control"
                @input="formatDistanzInput"
                required
              />
              <span class="input-group-text">Meter</span>
            </div>

            <!-- Dauer -->
            <div class="mb-4 input-group">
              <span class="input-group-text" title="Dauer (HH:mm:ss)">
                <span class="material-symbols-outlined"> timer </span>
              </span>
              <input
                type="number"
                class="form-control"
                placeholder="HH"
                title="Stunden"
                v-model.number="duration.h"
                min="0"
                max="23"
                step="1"
              />
              <input
                type="number"
                class="form-control"
                placeholder="mm"
                title="Minuten"
                v-model.number="duration.m"
                min="0"
                max="59"
                step="1"
              />
              <input
                type="number"
                class="form-control"
                placeholder="ss"
                title="Sekunden"
                v-model.number="duration.s"
                min="0"
                max="59"
                step="1"
              />
            </div>

            <!-- Buttons -->
            <div class="d-flex justify-content-between">
              <button
                type="button"
                class="btn btn-outline-secondary"
                @click="closeModal()"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                class="btn btn-ruderclub-dark-green d-flex align-items-center"
              >
                <i class="material-symbols-outlined me-2">save</i>Anlegen
              </button>
            </div>
          </form>
          <div class="resize-handler" @mousedown="startResize"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import { notify } from "@kyvg/vue3-notification";
import { collection, addDoc, doc, getDoc, getDocs } from "firebase/firestore";
import { firebaseAuth, firebaseDb } from "@/main";
import { onAuthStateChanged } from "firebase/auth";
import interact from "interactjs";

export const TRAININSEINHEIT_ANLEGEN_MODAL_ELEMENT_ID =
  "trainingseinheit-anlegen-modal";

export default {
  data() {
    return {
      modalInstance: null,
      x: 0,
      y: 0,
      modalWidth: 500, // Standardbreite des Modals
      modalHeight: 400, // Standardhöhe des Modals
      benutzer: null,
      form: {
        benutzer: null,
        titel: "Freies Training",
        datumUhrzeit: "",
        trainingsmethode: "",
        distanz: null,
        dauer: "", // Der zusammengesetzte String wird hier gespeichert
      },
      duration: {
        h: null,
        m: null,
        s: null,
      },
      benutzerListe: [],
      trainingsmethodenListe: [],
      istAdmin: false,
      targetComponentNameAfterSubmit: null,
    };
  },
  computed: {
    formattedDistanz: {
      get() {
        if (this.form.distanz === null) return "";
        return this.form.distanz
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      },
      set(value) {
        this.form.distanz = parseInt(value.replace(/\./g, ""), 10) || null;
      },
    },
  },
  async mounted() {
    interact(this.$refs.modalDialog).draggable({
      inertia: true,
      allowFrom: ".modal-header",
      modifiers: [
        interact.modifiers.restrictRect({
          restriction: "parent",
          endOnly: true,
        }),
      ],
      listeners: {
        move: this.dragMoveListener,
      },
    });

    onAuthStateChanged(firebaseAuth, async (user) => {
      if (!user) {
        this.$router.push("/login");
      }
    });
  },
  methods: {
    // Methode zum Öffnen des Modals und Entfernen des Backdrops
    async open(form = null, targetComponentNameAfterSubmit = null) {
      if (form) {
        const durationParts = form.dauer.split(":");
        this.form = form;
        this.form.dauer = null;

        this.duration.h = Number(durationParts[0]) || 0;
        this.duration.m = Number(durationParts[1]) || 0;
        this.duration.s = Number(durationParts[2]) || 0;

        this.targetComponentNameAfterSubmit = targetComponentNameAfterSubmit;
      } else {
        this.form.datumUhrzeit = new Date().toISOString().slice(0, 16);
      }

      try {
        const userDocRef = doc(
          firebaseDb,
          "benutzer",
          firebaseAuth.currentUser.uid
        );
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          this.benutzer = userDocSnap.data();
          this.istAdmin = this.benutzer.istAdmin;
          this.form.titel = this.benutzer.defaultTrainingsname;
          this.form.trainingsmethode =
            this.benutzer.defaultTrainingsmethodeFilter;

          if (this.istAdmin) {
            const benutzerSnap = await getDocs(
              collection(firebaseDb, "benutzer")
            );

            this.benutzerListe = benutzerSnap.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
          }

          this.form.benutzer = firebaseAuth.currentUser.uid;
        }

        const methodenSnap = await getDocs(
          collection(firebaseDb, "trainingsmethoden")
        );
        this.trainingsmethodenListe = methodenSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      } catch (error) {
        console.error("Fehler beim Laden der Daten:", error);
      }

      const modalElement = document.getElementById(
        TRAININSEINHEIT_ANLEGEN_MODAL_ELEMENT_ID
      );

      this.modalInstance = new Modal(modalElement, {
        backdrop: false,
        keyboard: false,
      });

      this.modalInstance.show();

      // Stelle sicher, dass das Modal in der Mitte startet
      this.$nextTick(() => {
        const modalDialog = this.$refs.modalDialog;

        if (modalDialog) {
          modalDialog.style.top = "50%";
          modalDialog.style.left = "50%";
          modalDialog.style.transform = "translate(-50%, -50%)";
        }
      });
    },

    dragMoveListener(event) {
      this.x += event.dx;
      this.y += event.dy;

      event.target.style.transform = `translate(${this.x}px, ${this.y}px)`;
    },

    closeModal() {
      this.x = 0;
      this.y = 0;
      this.$refs.modalDialog.style.transform = "none";

      if (this.modalInstance) {
        this.modalInstance.hide();
      }

      if (this.targetComponentNameAfterSubmit) {
        this.$router.push({
          name: this.targetComponentNameAfterSubmit,
        });
      }
    },

    startResize(event) {
      const modalContent = this.$refs.modalDialog;
      const initialWidth = modalContent.offsetWidth;
      const initialHeight = modalContent.offsetHeight;
      const initialX = event.clientX;
      const initialY = event.clientY;

      const mouseMoveHandler = (e) => {
        const width = initialWidth + (e.clientX - initialX);
        const height = initialHeight + (e.clientY - initialY);

        // Setze die neue Breite und Höhe
        modalContent.style.width = `${width}px`;
        modalContent.style.height = `${height}px`;
      };

      const mouseUpHandler = () => {
        document.removeEventListener("mousemove", mouseMoveHandler);
        document.removeEventListener("mouseup", mouseUpHandler);
      };

      document.addEventListener("mousemove", mouseMoveHandler);
      document.addEventListener("mouseup", mouseUpHandler);
    },

    formatDistanzInput(event) {
      const value = event.target.value.replace(/\./g, "");

      if (isNaN(value)) {
        return;
      }

      const tmpValue = parseInt(value, 10);

      if (tmpValue) {
        this.form.distanz = tmpValue;
      }
    },

    isValid(form) {
      return (
        form.benutzer &&
        form.titel &&
        form.trainingsmethode &&
        form.datumUhrzeit &&
        form.distanz &&
        form.dauer &&
        form.dauer !== "00:00:00" &&
        form.dauer !== ""
      );
    },

    async submitForm() {
      if (!this.duration.h) {
        this.duration.h = 0;
      }
      if (!this.duration.m) {
        this.duration.m = 0;
      }
      if (!this.duration.s) {
        this.duration.s = 0;
      }

      this.form.dauer = `${this.duration.h
        .toString()
        .padStart(2, "0")}:${this.duration.m
        .toString()
        .padStart(2, "0")}:${this.duration.s.toString().padStart(2, "0")}`;

      if (!this.isValid(this.form)) {
        notify({
          title: "Fehler",
          text: "Pflichtangaben fehlen.",
          type: "error",
          duration: 5000,
        });

        return;
      }

      try {
        await addDoc(collection(firebaseDb, "trainingseinheiten"), this.form);
        notify({
          title: "Erfolg",
          text: "Trainingseinheit erfolgreich gespeichert!",
          type: "success",
        });

        this.closeModal();
      } catch (error) {
        console.error("Fehler beim Speichern:", error);
        notify({
          title: "Fehler",
          text: "Trainingseinheit konnte nicht gespeichert werden.",
          type: "error",
        });
      }
    },
  },
};
</script>

<style scoped>
.modal {
  pointer-events: none !important;
}

.modal-dialog {
  position: fixed !important;
}

.modal-header {
  cursor: grab;
  user-select: none;
}

.modal-header:active {
  cursor: grabbing;
}

.modal-content {
  cursor: default;
  width: 100%;
  max-width: 90vw; /* Maximale Breite für das Modal */
  max-height: 90vh; /* Maximale Höhe für das Modal */
  overflow: hidden;
}

.resize-handler {
  width: 16px;
  height: 16px;
  background: #ddd;
  cursor: se-resize;
  position: absolute;
  bottom: 0;
  right: 0;
}

.modal-body {
  overflow-y: auto;
}
</style>
